import { useEffect, useRef, useState } from 'react';
import type { Client, ClientContact, ClientWithTasks, Project } from '../../types/models';
import { Avatar } from '../Avatar';
import { CloseIcon } from '../Icon';
import btn from '../../styles/buttons.module.css';
import form from '../../styles/forms.module.css';
import styles from './ClientEditor.module.css';

interface ClientEditorProps {
  client: ClientWithTasks;
  onClose: () => void;
  onSave: (patch: Partial<Client>) => void;
  onUploadLogo: (file: File) => Promise<void>;
}

export function ClientEditor({ client, onClose, onSave, onUploadLogo }: ClientEditorProps) {
  const [name, setName] = useState(client.name);
  const [initials, setInitials] = useState(client.initials);
  const [website, setWebsite] = useState(client.website ?? '');
  const [contactEmail, setContactEmail] = useState(client.contactEmail ?? '');
  const [phone, setPhone] = useState(client.phone ?? '');
  const [notes, setNotes] = useState(client.notes ?? '');
  const [projects, setProjects] = useState<Project[]>(client.projects);
  const [domains, setDomains] = useState<string[]>(client.additionalDomains ?? []);
  const [contacts, setContacts] = useState<ClientContact[]>(client.additionalContacts ?? []);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setProjectName = (id: string, value: string) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name: value } : p)));
  };
  const removeProject = (id: string) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };
  const addProject = () => {
    setProjects((prev) => [...prev, { id: crypto.randomUUID(), name: '' }]);
  };

  const setDomain = (index: number, value: string) => {
    setDomains((prev) => prev.map((d, i) => (i === index ? value : d)));
  };
  const removeDomain = (index: number) => {
    setDomains((prev) => prev.filter((_, i) => i !== index));
  };
  const addDomain = () => {
    setDomains((prev) => [...prev, '']);
  };

  const setContact = (id: string, patch: Partial<ClientContact>) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };
  const removeContact = (id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  };
  const addContact = () => {
    setContacts((prev) => [...prev, { id: crypto.randomUUID(), name: '', email: '', role: '' }]);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      await onUploadLogo(file);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    onSave({
      name: name.trim(),
      initials: initials.trim().toUpperCase().slice(0, 3),
      website: website.trim(),
      contactEmail: contactEmail.trim(),
      phone: phone.trim(),
      notes: notes.trim(),
      projects: projects
        .map((p) => ({ ...p, name: p.name.trim() }))
        .filter((p) => p.name.length > 0),
      additionalDomains: domains
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d.length > 0),
      additionalContacts: contacts
        .map((c) => ({
          id: c.id,
          name: (c.name ?? '').trim(),
          email: (c.email ?? '').trim(),
          role: (c.role ?? '').trim(),
        }))
        .filter((c) => c.name || c.email || c.role),
    });
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <span className={styles.title}>Edit client</span>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon size={16} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.logoRow}>
            <Avatar client={{ ...client, name, initials }} size={56} fontSize="1.1rem" />
            <div>
              <button
                type="button"
                className={styles.uploadBtn}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading…' : client.logoUrl ? 'Replace logo' : 'Upload logo'}
              </button>
              <div className={styles.uploadHint}>PNG or SVG, up to 25MB</div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className={styles.hiddenInput}
                onChange={(e) => void handleFile(e.target.files?.[0])}
              />
            </div>
          </div>

          <div className={form.grid2}>
            <div className={form.field}>
              <label className={form.label}>Name</label>
              <input
                className={form.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Initials</label>
              <input
                className={form.input}
                value={initials}
                maxLength={3}
                onChange={(e) => setInitials(e.target.value)}
              />
            </div>
          </div>

          <div className={form.field}>
            <label className={form.label}>Website</label>
            <input
              className={form.input}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://"
            />
          </div>

          <div className={form.grid2}>
            <div className={form.field}>
              <label className={form.label}>Contact email</label>
              <input
                className={form.input}
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="name@company.com"
              />
            </div>
            <div className={form.field}>
              <label className={form.label}>Phone</label>
              <input
                className={form.input}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(619) …"
              />
            </div>
          </div>

          <div className={form.field}>
            <label className={form.label}>Notes</label>
            <textarea
              className={form.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering about this client…"
            />
          </div>

          <div className={styles.sectionLabel}>Projects</div>
          <div className={styles.projectList}>
            {projects.map((p) => (
              <div key={p.id} className={styles.projectRow}>
                <input
                  className={`${form.input} ${styles.projectInput}`}
                  value={p.name}
                  onChange={(e) => setProjectName(p.id, e.target.value)}
                  placeholder="Project name"
                />
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => removeProject(p.id)}
                  aria-label="Remove project"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            ))}
            <button type="button" className={styles.addProject} onClick={addProject}>
              + Add project
            </button>
          </div>

          <div className={styles.sectionLabel}>Additional domains</div>
          <div className={styles.hint}>
            Inbound email from these domains routes to this client during triage.
          </div>
          <div className={styles.projectList}>
            {domains.map((d, i) => (
              <div key={i} className={styles.projectRow}>
                <input
                  className={`${form.input} ${styles.projectInput}`}
                  value={d}
                  onChange={(e) => setDomain(i, e.target.value)}
                  placeholder="example.com"
                />
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => removeDomain(i)}
                  aria-label="Remove domain"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            ))}
            <button type="button" className={styles.addProject} onClick={addDomain}>
              + Add domain
            </button>
          </div>

          <div className={styles.sectionLabel}>Additional contacts</div>
          <div className={styles.projectList}>
            {contacts.map((c) => (
              <div key={c.id} className={styles.contactRow}>
                <div className={styles.contactFields}>
                  <input
                    className={form.input}
                    value={c.name ?? ''}
                    onChange={(e) => setContact(c.id, { name: e.target.value })}
                    placeholder="Name"
                  />
                  <input
                    className={form.input}
                    value={c.email ?? ''}
                    onChange={(e) => setContact(c.id, { email: e.target.value })}
                    placeholder="name@company.com"
                  />
                  <input
                    className={form.input}
                    value={c.role ?? ''}
                    onChange={(e) => setContact(c.id, { role: e.target.value })}
                    placeholder="Role (optional)"
                  />
                </div>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => removeContact(c.id)}
                  aria-label="Remove contact"
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            ))}
            <button type="button" className={styles.addProject} onClick={addContact}>
              + Add contact
            </button>
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${btn.btn} ${btn.cta} ${styles.save}`}
            onClick={handleSave}
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
